// src/components/BookingCalendar.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Booking from './Booking';
import FilterPanel from './FilterPanel';
import './BookingCalendar.css';

const BookingCalendar = ({ bookings: initialBookings, currentUser }) => {
  const [bookings, setBookings] = useState(initialBookings || []);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('week'); // 'week' or 'month'
  const [currentWeekStart, setCurrentWeekStart] = useState(getStartOfWeek(new Date()));
  const [currentMonth, setCurrentMonth] = useState(getStartOfMonth(new Date()));
  const [showFilters, setShowFilters] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [collapsedDates, setCollapsedDates] = useState({});

  // Filters state that will be updated from FilterPanel
  const [filters, setFilters] = useState({
    pitch: '',
    date: '',
    sessionType: '',
  });

  // API URL configuration
  const API_URL = window.location.hostname === 'localhost'
    ? 'http://localhost:3001'
    : `https://${window.location.hostname}`;

  // Update when initial bookings change
  useEffect(() => {
    if (initialBookings && initialBookings.length > 0) {
      setBookings(initialBookings);
    } else {
      fetchBookings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialBookings]);

  // Filter bookings when filter criteria change
  useEffect(() => {
    filterBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, bookings, viewMode, currentWeekStart, currentMonth, refreshKey]);

  // Helper functions for date handling
  function getStartOfWeek(date) {
    const newDate = new Date(date);
    const day = newDate.getDay();
    const diff = newDate.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Sunday
    return new Date(newDate.setDate(diff));
  }

  function getEndOfWeek(startOfWeek) {
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    return endOfWeek;
  }

  function getStartOfMonth(date) {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }

  function formatDateString(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  function addDays(date, days) {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }

  function addMonths(date, months) {
    const result = new Date(date);
    result.setMonth(result.getMonth() + months);
    return result;
  }

  // Toggle collapse state for specific date groups
  const toggleDateGroup = (date) => {
    setCollapsedDates((prev) => ({ ...prev, [date]: !prev[date] }));
  };

  // Navigation functions
  const goToPreviousWeek = () => {
    setCurrentWeekStart((prev) => addDays(prev, -7));
    setRefreshKey((prev) => prev + 1);
  };
  const goToNextWeek = () => {
    setCurrentWeekStart((prev) => addDays(prev, 7));
    setRefreshKey((prev) => prev + 1);
  };
  const goToPreviousMonth = () => {
    setCurrentMonth((prev) => addMonths(prev, -1));
    setRefreshKey((prev) => prev + 1);
  };
  const goToNextMonth = () => {
    setCurrentMonth((prev) => addMonths(prev, 1));
    setRefreshKey((prev) => prev + 1);
  };
  const goToToday = () => {
    const today = new Date();
    setCurrentWeekStart(getStartOfWeek(today));
    setCurrentMonth(getStartOfMonth(today));
    setRefreshKey((prev) => prev + 1);
  };

  const fetchBookings = async () => {
    try {
      setLoading(true);
      setError('');
      // Auth is now via HttpOnly cookie. Keep sending the legacy
      // localStorage header too so a user mid-rollout (still on the
      // pre-cookie build) keeps working.
      const token = localStorage.getItem('footballBookingToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API_URL}/api/bookings`, {
        credentials: 'include',
        headers,
      });
      if (response.ok) {
        const data = await response.json();
        const sortedBookings = data.sort((a, b) => {
          const dateA = new Date(a.date + 'T' + a.start_time);
          const dateB = new Date(b.date + 'T' + b.start_time);
          return dateA - dateB;
        });
        setBookings(sortedBookings);
      } else {
        setError('Failed to load bookings. Please try again.');
      }
    } catch (err) {
      console.error('Error fetching bookings:', err);
      setError('Failed to fetch bookings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // booking.date can arrive as "YYYY-MM-DD" or as a full ISO timestamp like
  // "2026-04-26T00:00:00.000Z". String-comparing the raw value against a
  // "YYYY-MM-DD" range end excludes the last day of the range, because
  // "2026-04-26T..." sorts after "2026-04-26". Normalize before comparing.
  const dateKey = (value) => {
    if (!value) return '';
    const str = String(value);
    return str.length >= 10 ? str.slice(0, 10) : str;
  };

  const filterBookings = () => {
    if (!bookings || bookings.length === 0) {
      setFilteredBookings([]);
      return;
    }
    let filtered = [...bookings];

    if (filters.pitch) {
      filtered = filtered.filter((booking) => {
        const pitchId = parseInt(filters.pitch);
        const matchesPitchId = booking.pitch_id === pitchId;
        let matchesPitchName = false;
        if (pitchId === 1 && booking.pitch_name === 'Taylorstown Pitch') matchesPitchName = true;
        else if (pitchId === 2 && booking.pitch_name === 'Loughbeg Road Pitch') matchesPitchName = true;
        else if (pitchId === 3 && booking.pitch_name === 'Gym') matchesPitchName = true;
        if (pitchId === 1 && booking.pitch_name === 'Pitch 1') matchesPitchName = true;
        else if (pitchId === 2 && booking.pitch_name === 'Pitch 2') matchesPitchName = true;
        return matchesPitchId || matchesPitchName;
      });
    }

    if (filters.sessionType) {
      filtered = filtered.filter(
        (booking) => booking.session_type?.toLowerCase() === filters.sessionType.toLowerCase()
      );
    }

    if (filters.date) {
      const formattedSelectedDate = formatDateForComparison(filters.date);
      filtered = filtered.filter(
        (booking) => formatDateForComparison(booking.date) === formattedSelectedDate
      );
    } else if (viewMode === 'week') {
      const weekStart = formatDateString(currentWeekStart);
      const weekEnd = formatDateString(getEndOfWeek(currentWeekStart));
      filtered = filtered.filter((booking) => {
        const key = dateKey(booking.date);
        return key >= weekStart && key <= weekEnd;
      });
    } else if (viewMode === 'month') {
      const monthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const monthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
      const monthStartStr = formatDateString(monthStart);
      const monthEndStr = formatDateString(monthEnd);
      filtered = filtered.filter((booking) => {
        const key = dateKey(booking.date);
        return key >= monthStartStr && key <= monthEndStr;
      });
    }

    setFilteredBookings(filtered);
  };

  const formatDateForComparison = (dateString) => {
    if (!dateString) return '';
    try {
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
      if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString)) {
        const parts = dateString.split('/');
        const month = parseInt(parts[0], 10);
        const day = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);
        if (isNaN(month) || isNaN(day) || isNaN(year)) return '';
        return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      }
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (err) {
      console.error('Error formatting date:', err, dateString);
      return '';
    }
  };

  const handleDeleteBooking = async (id) => {
    if (currentUser?.role !== 'coach') {
      alert('Only coaches can delete bookings');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this booking?')) return;

    try {
      const token = localStorage.getItem('footballBookingToken');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const response = await fetch(`${API_URL}/api/bookings/${id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers,
      });
      if (response.ok) {
        setBookings(bookings.filter((booking) => booking.id !== id));
      } else {
        const errorData = await response.json();
        alert(errorData.error || 'Failed to delete booking');
      }
    } catch (err) {
      console.error('Error deleting booking:', err);
      alert('Failed to delete booking. Please check your connection and try again.');
    }
  };

  const handleViewModeChange = (mode) => {
    if (mode !== viewMode) {
      setViewMode(mode);
      setTimeout(() => setRefreshKey((prev) => prev + 1), 10);
    }
  };

  // Format for view header (week or month)
  const formatViewHeader = () => {
    if (viewMode === 'week') {
      const weekEnd = getEndOfWeek(currentWeekStart);
      const sameMonth = currentWeekStart.getMonth() === weekEnd.getMonth();
      if (sameMonth) {
        return `${currentWeekStart.getDate()} – ${weekEnd.getDate()} ${weekEnd.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}`;
      }
      return `${currentWeekStart.toLocaleDateString('en-GB', { month: 'short', day: 'numeric' })} – ${weekEnd.toLocaleDateString('en-GB', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    }
    return currentMonth.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  };

  // useCallback keeps the reference stable across renders so FilterPanel's
  // useEffect (which depends on onFilterChange) doesn't fire in an infinite
  // loop every time BookingCalendar re-renders.
  const handleFilterChange = useCallback((newFilters) => {
    setFilters(newFilters);
  }, []);

  // Group bookings by date for better organization. Normalize the key so that
  // bookings whose date arrives as "YYYY-MM-DD" and bookings whose date
  // arrives as a full ISO timestamp end up in the same bucket.
  const groupedBookings = useMemo(() => {
    const grouped = {};
    filteredBookings.forEach((booking) => {
      const date = dateKey(booking.date);
      if (!grouped[date]) grouped[date] = [];
      grouped[date].push(booking);
    });
    return Object.keys(grouped)
      .sort((a, b) => new Date(a) - new Date(b))
      .map((date) => ({ date, bookings: grouped[date] }));
  // dateKey is stable; ignore exhaustive-deps lint for it.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredBookings]);

  // ---- Derived stats for the summary strip ----
  const uniquePitches = useMemo(
    () => new Set(filteredBookings.map((b) => b.pitch_name || b.pitch_id)).size,
    [filteredBookings]
  );
  const uniqueCoaches = useMemo(
    () => new Set(filteredBookings.map((b) => b.coach_name || b.coach_id)).size,
    [filteredBookings]
  );
  const activeFilterCount = [filters.pitch, filters.date, filters.sessionType].filter(Boolean).length;

  // Helpers for day header rendering. booking.date may arrive as either a
  // strict "YYYY-MM-DD" string or a full ISO timestamp like
  // "2026-04-12T00:00:00.000Z". Use a regex so either form lands on a valid
  // local-time Date, avoiding NaN and the UTC-vs-local day-drift.
  const parseDay = (dateString) => {
    if (!dateString) return new Date(NaN);
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dateString));
    if (match) {
      const [, y, m, d] = match;
      return new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
    }
    const fallback = new Date(dateString);
    return isNaN(fallback.getTime()) ? new Date(NaN) : fallback;
  };

  const isToday = (d) => {
    const t = new Date();
    return (
      d.getFullYear() === t.getFullYear() &&
      d.getMonth() === t.getMonth() &&
      d.getDate() === t.getDate()
    );
  };

  return (
    <div className="sch-page">
      <div className="sch-topo" aria-hidden="true" />

      <div className="sch-shell">
        {/* ==================== Error alert ==================== */}
        {error && (
          <div className="sch-alert" role="alert">
            <span className="sch-alert-icon">!</span>
            <span>{error}</span>
            <button
              type="button"
              className="sch-alert-close"
              aria-label="Dismiss error"
              onClick={() => setError('')}
            >
              ×
            </button>
          </div>
        )}

        {/* ==================== Editorial header ==================== */}
        <header className="sch-header">
          <div className="sch-eyebrow">
            <span className="sch-dot" /> Schedule
          </div>
          <h1 className="sch-title">
            {viewMode === 'week' ? (
              <>This <em>week</em>.</>
            ) : (
              <>This <em>month</em>.</>
            )}
          </h1>
          <p className="sch-sub">
            Every session, every coach, every pitch — {formatViewHeader()}.
          </p>

          <dl className="sch-summary">
            <div className="sch-summary-cell">
              <dt>Sessions</dt>
              <dd>
                {filteredBookings.length}
                <small>booked</small>
              </dd>
            </div>
            <div className="sch-summary-cell">
              <dt>Pitches in use</dt>
              <dd>
                {uniquePitches}
                <small>facility</small>
              </dd>
            </div>
            <div className="sch-summary-cell">
              <dt>Coaches active</dt>
              <dd>
                {uniqueCoaches}
                <small>coach</small>
              </dd>
            </div>
          </dl>
        </header>

        {/* ==================== Controls ==================== */}
        <div className="sch-controls">
          <div className="sch-view-toggle" role="tablist">
            <button
              type="button"
              className={viewMode === 'week' ? 'is-active' : ''}
              onClick={() => handleViewModeChange('week')}
              aria-selected={viewMode === 'week'}
              role="tab"
            >
              Week
            </button>
            <button
              type="button"
              className={viewMode === 'month' ? 'is-active' : ''}
              onClick={() => handleViewModeChange('month')}
              aria-selected={viewMode === 'month'}
              role="tab"
            >
              Month
            </button>
          </div>

          <div className="sch-date-nav">
            <button
              type="button"
              className="sch-nav-arrow"
              onClick={viewMode === 'week' ? goToPreviousWeek : goToPreviousMonth}
              aria-label={viewMode === 'week' ? 'Previous week' : 'Previous month'}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18L9 12L15 6" />
              </svg>
            </button>
            <div className="sch-date-range" aria-live="polite">
              {formatViewHeader()}
            </div>
            <button
              type="button"
              className="sch-nav-arrow"
              onClick={viewMode === 'week' ? goToNextWeek : goToNextMonth}
              aria-label={viewMode === 'week' ? 'Next week' : 'Next month'}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 6L15 12L9 18" />
              </svg>
            </button>
          </div>

          <button type="button" className="sch-today-btn" onClick={goToToday}>
            Today
          </button>
        </div>

        {/* ==================== Filter bar ==================== */}
        <div className="sch-filter-bar">
          <button
            type="button"
            className={`sch-filter-toggle ${showFilters ? 'is-open' : ''}`}
            onClick={() => setShowFilters((s) => !s)}
            aria-expanded={showFilters}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="4" y1="6" x2="20" y2="6" />
              <line x1="7" y1="12" x2="17" y2="12" />
              <line x1="10" y1="18" x2="14" y2="18" />
            </svg>
            Filters
            {activeFilterCount > 0 && (
              <span className="sch-filter-count">· {activeFilterCount} active</span>
            )}
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
        </div>

        <div className={`sch-filter-panel ${showFilters ? 'is-open' : ''}`}>
          <FilterPanel onFilterChange={handleFilterChange} getBookings={fetchBookings} />
        </div>

        {/* ==================== Bookings / day groups ==================== */}
        <div role="tabpanel">
          {loading ? (
            <div className="sch-loading">
              <div className="sch-loading-spinner" />
              <p>Loading bookings…</p>
            </div>
          ) : groupedBookings.length > 0 ? (
            <div className="sch-bookings">
              {groupedBookings.map((group) => {
                const d = parseDay(group.date);
                const dow = d.toLocaleDateString('en-GB', { weekday: 'short' });
                const dayNum = String(d.getDate()).padStart(2, '0');
                const monthLabel = d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
                const isCollapsed = collapsedDates[group.date];
                return (
                  <section key={group.date} className="sch-day">
                    <div className="sch-day-head">
                      <span className="sch-day-dow">{dow}</span>
                      <span className="sch-day-num">{dayNum}</span>
                      <div className="sch-day-meta">
                        <div className="sch-day-month">{monthLabel}</div>
                        <div className="sch-day-count">
                          {group.bookings.length} session{group.bookings.length !== 1 ? 's' : ''}
                        </div>
                      </div>
                      {isToday(d) && <span className="sch-day-today">Today</span>}
                      <button
                        type="button"
                        className={`sch-day-collapse ${isCollapsed ? 'is-collapsed' : ''}`}
                        onClick={() => toggleDateGroup(group.date)}
                        aria-expanded={!isCollapsed}
                      >
                        {isCollapsed ? 'Show' : 'Hide'}
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M6 9l6 6 6-6" />
                        </svg>
                      </button>
                    </div>

                    {!isCollapsed && (
                      <div className="sch-day-bookings">
                        {group.bookings.map((booking) => (
                          <Booking
                            key={booking.id}
                            booking={booking}
                            onDelete={() => handleDeleteBooking(booking.id)}
                          />
                        ))}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          ) : (
            <div className="sch-empty">
              <div className="sch-empty-eyebrow">Nothing scheduled</div>
              <h2 className="sch-empty-title">
                A <em>quiet</em> {viewMode === 'week' ? 'week' : 'month'}.
              </h2>
              <p className="sch-empty-message">
                {filters.pitch || filters.date || filters.sessionType
                  ? 'Try clearing your filters, or jump to a different period with the arrows above.'
                  : viewMode === 'week'
                    ? 'No sessions booked for this week. Use the arrows to browse, or add a new booking.'
                    : 'No sessions booked for this month. Use the arrows to browse, or add a new booking.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ==================== FAB ==================== */}
      {currentUser?.role === 'coach' && (
        <a href="/add-booking" className="sch-fab" aria-label="Add new booking">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          New booking
        </a>
      )}
    </div>
  );
};

export default BookingCalendar;
